import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, Tag, Calendar, CalendarCheck, MessageCircle, X as XIcon, Maximize2 } from "lucide-react";
import { track } from "@/components/landing/shared";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PromotionsPublicPage() {
  const [promotions, setPromotions] = useState([]);
  const [generalContact, setGeneralContact] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 226 — Lightbox para imagen ampliada.
  const [lightbox, setLightbox] = useState(null);  // {src, alt} | null

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/promotions/public`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/public/general-contact`).then(r => r.data).catch(() => null),
    ]).then(([prms, gc]) => {
      setPromotions(prms);
      setGeneralContact(gc);
      setLoading(false);
    });
  }, []);

  // Iter 226 — Cerrar con ESC. Bloquea scroll del body cuando está abierto.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox]);

  const waPhoneClean = generalContact?.whatsapp_phone_clean || "";

  const buildPromoLink = (p) => {
    const params = new URLSearchParams({
      promo_id: p.id || "",
      promo_titulo: p.title || "",
      promo_categoria: p.category || "",
      promo_precio: p.highlight_text || "",
      promo_origen: "promocion_postventa",
    }).toString();
    return `/cita?${params}`;
  };

  return (
    <div data-testid="promotions-public-page">
      {/* Hero */}
      <section className="relative bg-slate-900 py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[11px] uppercase tracking-[0.3em] text-red-500 font-semibold">Ascauto Servicio Oficial</span>
          <h1 className="heading-industrial text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 uppercase tracking-tight">Ofertas Postventa</h1>
          <p className="text-base text-slate-400 mt-4 max-w-2xl mx-auto leading-relaxed">Aprovecha nuestras promociones activas en mecanica, chapa y pintura, recambios y mantenimiento.</p>
        </div>
      </section>

      {/* Promotions grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16 text-sm text-slate-400">Cargando ofertas...</div>
          ) : promotions.length === 0 ? (
            <div className="text-center py-16">
              <Tag className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-700">No hay ofertas activas en este momento</h2>
              <p className="text-sm text-slate-400 mt-2">Vuelve pronto para descubrir nuestras promociones.</p>
              <Link to="/cita" className="mt-6 inline-block">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-xs uppercase tracking-wider font-bold h-11 px-8">
                  Reservar Cita
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {promotions.map((p, i) => {
                const imgSrc = getImageUrl(p.image_url);
                return (
                  <div key={p.id || i} className="group border border-slate-200 hover:border-red-200 hover:shadow-lg transition-all duration-300 flex flex-col" data-testid={`promo-public-${i}`}>
                    {p.image_url && (
                      <button
                        type="button"
                        className="aspect-[16/9] overflow-hidden bg-slate-100 relative cursor-zoom-in block w-full"
                        onClick={() => {
                          track("promo_image_zoom", p.title);
                          setLightbox({ src: imgSrc, alt: p.title || "Promoción" });
                        }}
                        data-testid={`promo-image-${i}`}
                        aria-label={`Ver imagen ampliada de ${p.title || "promoción"}`}
                      >
                        <img src={imgSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <span
                          className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                          aria-hidden="true"
                        >
                          <Maximize2 className="w-3 h-3" /> Ver imagen
                        </span>
                      </button>
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-start gap-2 mb-3">
                        <h3 className="text-base font-bold text-slate-900 uppercase tracking-wide leading-tight flex-1">{p.title}</h3>
                        {p.highlight_text && <span className="text-[10px] uppercase tracking-wider font-bold bg-red-600 text-white px-2.5 py-1 rounded-sm flex-shrink-0">{p.highlight_text}</span>}
                      </div>
                      {p.description && <p className="text-sm text-slate-500 leading-relaxed mb-4">{p.description}</p>}
                      {p.category && <span className="inline-block text-[9px] uppercase tracking-wider font-semibold text-slate-400 border border-slate-200 px-2 py-0.5 rounded-sm mb-3">{p.category}</span>}
                      {p.end_date && (
                        <span className="text-[10px] text-slate-400 flex items-center gap-1 mb-4">
                          <Calendar className="w-3 h-3" /> Hasta {new Date(p.end_date).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}
                        </span>
                      )}
                      <div className="mt-auto pt-4 border-t border-slate-100">
                        <Link to={buildPromoLink(p)}
                          className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-3 transition-colors w-full"
                          data-testid={`promo-book-${i}`}
                          onClick={() => track("promo_book_click", p.title)}>
                          <CalendarCheck className="w-4 h-4" /> Reservar cita con esta oferta
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16 lg:py-20 bg-slate-900 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-industrial text-xl sm:text-2xl font-bold text-white uppercase tracking-wide">No dejes pasar estas ofertas</h2>
          <p className="text-sm text-slate-400 mt-3 mb-8">Reserva tu cita y aprovecha nuestras promociones activas.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/cita">
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-10 w-full sm:w-auto" data-testid="promo-cta-cita">
                Reservar Cita
              </Button>
            </Link>
            {waPhoneClean && (
              <a href={`https://wa.me/${waPhoneClean}`} target="_blank" rel="noopener noreferrer" onClick={() => track("promo_whatsapp", "whatsapp")}
                className="inline-flex items-center justify-center gap-2 border border-slate-600 text-slate-300 hover:bg-white/5 hover:border-slate-400 rounded-none text-sm uppercase tracking-wider font-bold h-14 px-10 transition-colors"
                data-testid="promo-cta-whatsapp">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Iter 226 — Lightbox / modal de imagen ampliada */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 animate-in fade-in duration-150"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Imagen ampliada de la promoción"
          data-testid="promo-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-full transition-colors"
            aria-label="Cerrar imagen ampliada"
            data-testid="promo-lightbox-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            className="block max-w-[90vw] max-h-[85vh] w-auto h-auto object-contain shadow-2xl"
            data-testid="promo-lightbox-image"
          />
        </div>
      )}
    </div>
  );
}
